import { computed, nextTick, onScopeDispose, ref, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import {
  isUpdateDraft,
  liveHasChanges,
  liveSummaryCounts,
  othersCardGroups,
} from '@/domain/menu-import/live-update';
import type { IUpdateSummary } from '@/domain/menu-import/live-update';
import { itemFiltersFor } from '@/domain/menu-import/menu-draft';
import type {
  IDraftSummary,
  TItemFilter,
} from '@/domain/menu-import/menu-draft';
import { openQuestions } from '@/domain/menu-import/workspace-view';
import type { IWorkspaceBarUpdate } from '@/domain/menu-import/workspace-view';
import type { IMenuDraft } from '@/interfaces/IMenuImport';
import { useMenuImport } from './menu-import-context';

/** Before a draft exists: only «Todos». */
const ALL_ONLY: TItemFilter[] = ['all'];

export interface ILiveUpdateViewOptions {
  draft: () => IMenuDraft | null;
  summary: () => IDraftSummary | null;
  counts: () => Record<TItemFilter, number> | null;
  /** Brings the card of the menu's dishes left out of the photos into view. */
  scrollToOthers: () => Promise<void> | void;
}

export interface IUseLiveUpdateView {
  /** The draft was matched with the store's menu (an update). */
  isUpdate: ComputedRef<boolean>;
  /** The filter chips offered. */
  filters: ComputedRef<TItemFilter[]>;
  /** The `update` input of `workspaceBarModel`. */
  barUpdate: ComputedRef<IWorkspaceBarUpdate | null>;
  /** An update that changes nothing (the «Su menú ya está al día.» end card). */
  upToDate: ComputedRef<boolean>;
  /** What the summary card says in an update. */
  summaryUpdate: ComputedRef<IUpdateSummary | null>;
  /** «Parece un menú nuevo» waits for an answer (the end card waits too). */
  newMenuOpen: ComputedRef<boolean>;
  /** The reading screen compares with the store's menu at the end. */
  readingUpdate: ComputedRef<boolean>;
  /** The others card is open (`.sync` with the list). */
  othersExpanded: Ref<boolean>;
  /** «{count} platos no están en las fotos»: the list, «Todos», the card open and in view. */
  showOthers(): Promise<void>;
}

/**
 * The workspace's wiring of an update session (plan §3.5, §7.5): which
 * filter chips it offers (and «Todos» again when the chosen one is gone),
 * what the bottom bar, the end card and the summary need, and the others
 * card. Call it in the workspace's setup.
 */
export function useLiveUpdateView(
  options: ILiveUpdateViewOptions
): IUseLiveUpdateView {
  const ctx = useMenuImport();
  const othersExpanded = ref(false);
  let alive = true;

  const isUpdate = computed(() => isUpdateDraft(options.draft()));
  const hasChanges = computed(() => {
    const draft = options.draft();
    return draft !== null && isUpdateDraft(draft) && liveHasChanges(draft);
  });
  const filters = computed(() => {
    const draft = options.draft();
    return draft ? itemFiltersFor(draft, ctx.filter.value) : ALL_ONLY;
  });
  const readingUpdate = computed(
    () => isUpdate.value || ctx.existingItems.value > 0
  );
  /**
   * A store with dishes says «Publicar cambios» from the start (while its
   * photos are read, before the comparison); «Terminar» only once compared.
   */
  const barUpdate = computed<IWorkspaceBarUpdate | null>(() => {
    const summary = options.summary();
    if (!readingUpdate.value || !summary) return null;
    return {
      hasChanges: isUpdate.value ? hasChanges.value : true,
      toCheck: summary.toCheck,
      refreshing: ctx.liveRefreshing.value,
    };
  });
  const upToDate = computed(() => isUpdate.value && !hasChanges.value);
  const newMenuOpen = computed(() => {
    const draft = options.draft();
    return (
      draft !== null &&
      openQuestions(draft).some((question) => question.kind === 'new_menu')
    );
  });
  const summaryUpdate = computed<IUpdateSummary | null>(() => {
    const draft = options.draft();
    const counts = options.counts();
    if (!draft || !counts || !isUpdateDraft(draft)) return null;
    return {
      priceChanged: counts.priceChanged,
      added: counts.new,
      heldLeft: liveSummaryCounts(draft).heldLeft,
      others: othersCardGroups(draft).total,
      newMenuOpen: newMenuOpen.value,
    };
  });

  // A chip that is not offered any more (the draft became an update, «Sin
  // precio» emptied) gives way to «Todos».
  watch(
    filters,
    (offered) => {
      if (!offered.includes(ctx.filter.value)) ctx.filter.value = 'all';
    },
    { immediate: true }
  );

  async function showOthers() {
    ctx.mode.value = 'list';
    ctx.filter.value = 'all';
    othersExpanded.value = true;
    await nextTick();
    if (!alive) return;
    await options.scrollToOthers();
  }

  onScopeDispose(() => {
    alive = false;
  });

  return {
    isUpdate,
    filters,
    barUpdate,
    upToDate,
    summaryUpdate,
    newMenuOpen,
    readingUpdate,
    othersExpanded,
    showOthers,
  };
}
